import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { useFeatureTogglingContext } from '@spacefill/shared/src/utils/FeatureToggling';
import { createRoutesFromObject } from '@spacefill/shared/src/utils/RouterUtils';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { Loader } from '@spacefill/uikit/src/components/Loader';

import { getCarrierRouter } from './routes/Carriers';
import { getCustomerRouter } from './routes/Customer';
import { getEmailToOrderRouter } from './routes/EmailToOrder';
import { getEmailToTransportRouter } from './routes/EmailToTransport';
import { getExternalKeysRouter } from './routes/ExternalKeys';
import { getIntegrationsRouter } from './routes/Integrations';
import { getLogisticManagementRouter } from './routes/LogisticManagement';
import { getLogisticProviderRouter } from './routes/LogisticProviders';

const LogoutPage = async () =>
    import('@spacefill/shared/src/pages/Logout').then((module) => ({
        Component: module.Logout,
    }));
const EnvView = async () =>
    import('@spacefill/shared/src/pages/Env').then((module) => ({
        Component: module.EnvView,
    }));
const SignInPage = async () =>
    import('@spacefill/shared/src/pages/SignIn').then((module) => ({
        Component: module.SignInPage,
    }));

const AdminConsoleLoggedUserLayout = async () =>
    await import('../layout/AppLayout').then((module) => ({
        Component: module.AdminConsoleLoggedUserLayout,
    }));
const AdminConsoleUnloggedUserLayout = async () =>
    await import('../layout/AppLayout').then((module) => ({
        Component: module.AdminConsoleUnloggedUserLayout,
    }));

const ProfilePage = async () =>
    await import('../pages/ProfilePage').then((module) => ({
        Component: module.ProfilePage,
    }));
const UsersList = async () =>
    import('../pages/users/List').then((module) => ({
        Component: module.UsersList,
    }));
const AdminConsoleThemeProvider = async () =>
    import('../layout/AppLayout').then((module) => ({
        Component: module.AdminConsoleAppLayout,
    }));
const DashbordsView = async () =>
    import('../pages/dashboards/Page').then((module) => ({
        Component: module.DashboardsPage,
    }));

const DashbordsRedirectLougout = async () =>
    import('../pages/dashboards/RedirectLoggedOutDashboard').then((module) => ({
        Component: module.RedirectLoggedOutDashboard,
    }));

const getLoggedUserRoutes = (user, features) => {
    return [
        // Index
        {
            path: '/',
            element: <Navigate to='/logistic-management/orders/' />,
        },
        // Logout
        {
            path: '/logout/',
            lazy: LogoutPage,
        },
        // Profil
        {
            path: '/profile/',
            lazy: ProfilePage,
            redirectFrom: ['/profile'],
        },
        // Users
        {
            path: '/users/',
            lazy: UsersList,
            redirectFrom: ['/users'],
        },
        // Logistics providers
        ...getLogisticProviderRouter(user, features),

        // Customers
        ...getCustomerRouter(user, features),

        // Logistic management
        ...getLogisticManagementRouter(user, features),

        // Integrations
        ...getIntegrationsRouter(user, features),
        // Transport
        ...getCarrierRouter(user, features),

        // email-to-order
        ...getEmailToOrderRouter(user, features),

        // External Keys
        ...getExternalKeysRouter(user, features),

        // Email to transport
        ...getEmailToTransportRouter(user, features),

        {
            path: '/analytics-new/',
            element: <Navigate to='/analytics/' />,
        },
        {
            path: '/analytics/',
            lazy: DashbordsView,
        },
        {
            path: '/analytics/redirect/:supersetKey',
            lazy: DashbordsRedirectLougout,
        },

        // Default
        {
            path: '*',
            element: <Navigate to='/logistic-management/orders/' />,
        },
    ];
};

const DefaultNavigate = () => {
    const locationPathName = window?.location?.pathname;
    return (
        <Navigate
            to={
                locationPathName && locationPathName !== '/' && !locationPathName.includes('logout')
                    ? `/sign-in/?next-url=${encodeURIComponent(locationPathName)}`
                    : '/sign-in/'
            }
        />
    );
};

const unknownUserRoutes = [
    {
        path: '/sign-in/',
        lazy: SignInPage,
    },
    // Version
    {
        path: '/environment/',
        lazy: EnvView,
    },
    {
        path: '*',
        element: <DefaultNavigate />,
    },
];

const RouterContext = () => {
    const { user, isCheckingSession } = useUserContext();
    const { features } = useFeatureTogglingContext();

    if (isCheckingSession) {
        return (
            <div className='h-screen w-screen'>
                <Loader />
            </div>
        );
    }

    let routes;

    if (user?.logged) {
        routes = getLoggedUserRoutes(user, features);
    } else {
        routes = unknownUserRoutes;
    }

    const formattedRoutes = createRoutesFromObject(routes);

    let router;
    if (user?.logged) {
        router = createBrowserRouter(
            [
                {
                    lazy: AdminConsoleLoggedUserLayout,
                    children: [
                        // Version
                        {
                            path: '/environment/',
                            lazy: EnvView,
                        },
                        {
                            path: '/environment',
                            element: <Navigate to='/environment/' />,
                        },
                        {
                            lazy: AdminConsoleThemeProvider,
                            children: formattedRoutes,
                        },
                    ],
                },
            ],
            {
                basename: import.meta.env?.VITE_ADMIN_CONSOLE_ROUTER_BASE_NAME,
            }
        );
    } else {
        router = createBrowserRouter(
            [
                {
                    lazy: AdminConsoleUnloggedUserLayout,
                    children: formattedRoutes,
                },
            ],
            {
                basename: import.meta.env?.VITE_ADMIN_CONSOLE_ROUTER_BASE_NAME,
            }
        );
    }

    return <RouterProvider router={router} />;
};

export { RouterContext };
